import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import {
  Lock,
  Unlock,
  AlertTriangle,
  RefreshCw,
  User,
  Clock,
  Shield,
  Globe,
  CheckCircle,
  XCircle
} from 'lucide-react';

const LockedAccounts = ({ superAdminToken }) => {
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [atRiskAccounts, setAtRiskAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchLockedAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/super-admin/locked-accounts', {
        headers: {
          'Authorization': `Bearer ${superAdminToken}`
        }
      });
      
      setLockedAccounts(response.data.locked_accounts || []);
      setAtRiskAccounts(response.data.at_risk_accounts || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch locked accounts');
    } finally {
      setLoading(false);
    }
  }, [superAdminToken]);

  useEffect(() => {
    fetchLockedAccounts();
  }, [fetchLockedAccounts]);

  const handleUnlock = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to unlock the account for ${email}?`)) {
      return;
    }
    
    setActionLoading(userId);
    setError(null);
    
    try {
      await api.post(`/super-admin/users/${userId}/unlock`, {}, {
        headers: {
          'Authorization': `Bearer ${superAdminToken}`
        }
      });
      
      setSuccessMessage(`Account for ${email} has been unlocked successfully.`);
      setTimeout(() => setSuccessMessage(null), 5000);
      fetchLockedAccounts();
    } catch (err) {
      setError(err.message || 'Failed to unlock account');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetAttempts = async (userId, email) => {
    if (!window.confirm(`Reset failed login attempts for ${email}?`)) {
      return;
    }
    
    setActionLoading(userId);
    setError(null);
    
    try {
      await api.post(`/super-admin/users/${userId}/reset-login-attempts`, {}, {
        headers: {
          'Authorization': `Bearer ${superAdminToken}`
        }
      });
      
      setSuccessMessage(`Login attempts reset for ${email}.`);
      setTimeout(() => setSuccessMessage(null), 5000);
      fetchLockedAccounts();
    } catch (err) {
      setError(err.message || 'Failed to reset login attempts');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getTimeRemaining = (lockedUntil) => {
    if (!lockedUntil) return '-';
    const now = new Date();
    const lockEnd = new Date(lockedUntil);
    const diffMs = lockEnd - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const minutes = Math.ceil(diffMs / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const styles = {
    container: {
      padding: '1.5rem'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    refreshBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#f1f5f9',
      border: '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontSize: '0.875rem',
      color: '#475569'
    },
    section: {
      marginBottom: '2rem'
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#334155',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: '0.75rem 1rem',
      textAlign: 'left',
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase'
    },
    td: {
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #f1f5f9',
      fontSize: '0.875rem',
      color: '#334155'
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.5rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    badgeLocked: {
      backgroundColor: '#fee2e2',
      color: '#dc2626'
    },
    badgeAtRisk: {
      backgroundColor: '#fef3c7',
      color: '#d97706'
    },
    unlockBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.375rem 0.75rem',
      backgroundColor: '#10b981',
      color: 'white',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    resetBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.375rem 0.75rem',
      backgroundColor: '#6366f1',
      color: 'white',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    alert: {
      padding: '1rem',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    alertError: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
      border: '1px solid #fecaca'
    },
    alertSuccess: {
      backgroundColor: '#dcfce7',
      color: '#16a34a',
      border: '1px solid #bbf7d0'
    },
    emptyState: {
      padding: '3rem',
      textAlign: 'center',
      color: '#64748b'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    statCard: {
      backgroundColor: 'white',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    statIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1e293b'
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#64748b'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
          <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading locked accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <Lock size={24} style={{ color: '#dc2626' }} />
          Account Lockout Management
        </h1>
        <button 
          style={styles.refreshBtn} 
          onClick={fetchLockedAccounts}
          disabled={loading}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ ...styles.alert, ...styles.alertError }}>
          <XCircle size={18} />
          {error}
        </div>
      )}
      
      {successMessage && (
        <div style={{ ...styles.alert, ...styles.alertSuccess }}>
          <CheckCircle size={18} />
          {successMessage}
        </div>
      )}

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fee2e2' }}>
            <Lock size={24} style={{ color: '#dc2626' }} />
          </div>
          <div>
            <p style={styles.statValue}>{lockedAccounts.length}</p>
            <p style={styles.statLabel}>Currently Locked</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7' }}>
            <AlertTriangle size={24} style={{ color: '#d97706' }} />
          </div>
          <div>
            <p style={styles.statValue}>{atRiskAccounts.length}</p>
            <p style={styles.statLabel}>At Risk (3+ failures)</p>
          </div>
        </div>
      </div>

      {/* Locked Accounts */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Lock size={18} style={{ color: '#dc2626' }} />
          Locked Accounts
        </h2>
        <div style={styles.card}>
          {lockedAccounts.length === 0 ? (
            <div style={styles.emptyState}>
              <Shield size={48} style={{ color: '#10b981', marginBottom: '1rem' }} />
              <p style={{ fontWeight: '500', color: '#334155' }}>No locked accounts</p>
              <p style={{ fontSize: '0.875rem' }}>All user accounts are currently accessible.</p>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Team</th>
                  <th style={styles.th}>Failed Attempts</th>
                  <th style={styles.th}>Locked Until</th>
                  <th style={styles.th}>Time Remaining</th>
                  <th style={styles.th}>Last IP</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {lockedAccounts.map((account) => (
                  <tr key={account.id}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <User size={16} style={{ color: '#64748b' }} />
                        <div>
                          <p style={{ fontWeight: '500' }}>{account.name || 'Unknown'}</p>
                          <p style={{ fontSize: '0.75rem', color: '#64748b' }}>{account.email}</p>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{account.team_name || '-'}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...styles.badgeLocked }}>
                        {account.failed_attempts} attempts
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Clock size={14} style={{ color: '#64748b' }} />
                        {formatDate(account.locked_until)}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontWeight: '500', color: '#dc2626' }}>
                        {getTimeRemaining(account.locked_until)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Globe size={14} style={{ color: '#64748b' }} />
                        {account.last_ip || '-'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={{
                          ...styles.unlockBtn,
                          opacity: actionLoading === account.id ? 0.5 : 1
                        }}
                        onClick={() => handleUnlock(account.id, account.email)}
                        disabled={actionLoading === account.id}
                      >
                        <Unlock size={14} />
                        {actionLoading === account.id ? 'Unlocking...' : 'Unlock'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* At-Risk Accounts */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <AlertTriangle size={18} style={{ color: '#d97706' }} />
          At-Risk Accounts (3+ Failed Attempts)
        </h2>
        <div style={styles.card}>
          {atRiskAccounts.length === 0 ? (
            <div style={styles.emptyState}>
              <CheckCircle size={48} style={{ color: '#10b981', marginBottom: '1rem' }} />
              <p style={{ fontWeight: '500', color: '#334155' }}>No at-risk accounts</p>
              <p style={{ fontSize: '0.875rem' }}>No accounts have multiple failed login attempts.</p>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Team</th>
                  <th style={styles.th}>Failed Attempts</th>
                  <th style={styles.th}>Last Failed Login</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {atRiskAccounts.map((account) => (
                  <tr key={account.id}>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <User size={16} style={{ color: '#64748b' }} />
                        <div>
                          <p style={{ fontWeight: '500' }}>{account.name || 'Unknown'}</p>
                          <p style={{ fontSize: '0.75rem', color: '#64748b' }}>{account.email}</p>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{account.team_name || '-'}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, ...styles.badgeAtRisk }}>
                        {account.failed_attempts} / 5 attempts
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Clock size={14} style={{ color: '#64748b' }} />
                        {formatDate(account.last_failed_at)}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={{
                          ...styles.resetBtn,
                          opacity: actionLoading === account.id ? 0.5 : 1
                        }}
                        onClick={() => handleResetAttempts(account.id, account.email)}
                        disabled={actionLoading === account.id}
                      >
                        <RefreshCw size={14} />
                        {actionLoading === account.id ? 'Resetting...' : 'Reset'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default LockedAccounts;
